import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { paginateQuery } from '@/lib/paginateSupabase';
import { useMemo } from 'react';

export type ConversationTool = 'lia' | 'challenge' | 'prototype' | 'all';

export interface AuditFilters {
  search?: string;
  toolType?: ConversationTool;
  organizationId?: string;
  cohortId?: string;
  userId?: string;
  dateFrom?: string;
  dateTo?: string;
}

export interface ConversationRow {
  session_id: string;
  user_id: string;
  user_name: string;
  user_email: string;
  organization_id: string | null;
  organization_name: string | null;
  context_type: string | null;
  tool_label: string;
  title: string | null;
  created_at: string;
  updated_at: string;
  message_count: number;
  cohort_names: string[];
}

export interface ConversationMessage {
  id: string;
  role: string;
  content: string;
  model: string | null;
  created_at: string;
  metadata: any;
}

const TOOL_LABELS: Record<string, string> = {
  lia: 'LIA',
  challenge: 'Laboratorio de Retos y Soluciones',
  prototype: 'Agente de Prototipado',
};

export function toolLabel(contextType: string | null): string {
  if (!contextType) return 'Otro';
  return TOOL_LABELS[contextType] || contextType;
}

// Lista de sesiones (cabeceras) con filtros y conteo de mensajes
export function useConversationsAudit(filters: AuditFilters) {
  const { currentRole, profile } = useAuth();
  const isSuperAdmin = currentRole === 'super_admin';
  const isAdmin = currentRole === 'admin';

  return useQuery({
    queryKey: ['ai-conversations-audit', filters, currentRole, profile?.organization_id],
    enabled: isSuperAdmin || isAdmin,
    queryFn: async (): Promise<ConversationRow[]> => {
      const buildSessionsQuery = () => {
        let q = supabase
          .from('chat_sessions')
          .select(`
            id, user_id, organization_id, context_type, title, created_at, updated_at,
            profiles!chat_sessions_user_id_fkey(id, full_name, email),
            organizations(id, name)
          `)
          .order('updated_at', { ascending: false });

        if (filters.toolType && filters.toolType !== 'all') {
          q = q.eq('context_type', filters.toolType);
        }
        if (filters.organizationId && filters.organizationId !== 'all') {
          q = q.eq('organization_id', filters.organizationId);
        }
        if (filters.userId) {
          q = q.eq('user_id', filters.userId);
        }
        if (filters.dateFrom) q = q.gte('created_at', filters.dateFrom);
        if (filters.dateTo) q = q.lte('created_at', filters.dateTo);

        // Admin de org: filtro adicional defensivo (RLS también filtra)
        if (isAdmin && !isSuperAdmin && profile?.organization_id) {
          q = q.eq('organization_id', profile.organization_id);
        }
        return q;
      };

      const sessions = await paginateQuery<any>((from, to) => buildSessionsQuery().range(from, to));
      if (sessions.length === 0) return [];

      const sessionIds = sessions.map((s: any) => s.id);
      const userIds = [...new Set(sessions.map((s: any) => s.user_id))];

      // Conteo de mensajes por sesión (paginado por chunks para evitar límite default de 1000)
      const msgCount = new Map<string, number>();
      const CHUNK = 50;
      for (let i = 0; i < sessionIds.length; i += CHUNK) {
        const slice = sessionIds.slice(i, i + CHUNK);
        const { data: msgs } = await supabase
          .from('chat_messages')
          .select('session_id')
          .in('session_id', slice)
          .limit(100000);
        (msgs || []).forEach((m: any) => msgCount.set(m.session_id, (msgCount.get(m.session_id) || 0) + 1));
      }

      // Cohorts de cada usuario — chunkeamos userIds para evitar URLs gigantes
      const cohortsByUser = new Map<string, string[]>();
      for (let i = 0; i < userIds.length; i += 200) {
        const slice = userIds.slice(i, i + 200);
        const { data: cohortRows } = await supabase
          .from('cohort_participants')
          .select('user_id, cohorts(name)')
          .in('user_id', slice);
        (cohortRows || []).forEach((cr: any) => {
          const arr = cohortsByUser.get(cr.user_id) || [];
          if (cr.cohorts?.name) arr.push(cr.cohorts.name);
          cohortsByUser.set(cr.user_id, arr);
        });
      }

      let rows: ConversationRow[] = sessions.map((s: any) => ({
        session_id: s.id,
        user_id: s.user_id,
        user_name: s.profiles?.full_name || 'Sin nombre',
        user_email: s.profiles?.email || '',
        organization_id: s.organization_id,
        organization_name: s.organizations?.name || null,
        context_type: s.context_type,
        tool_label: toolLabel(s.context_type),
        title: s.title,
        created_at: s.created_at,
        updated_at: s.updated_at,
        message_count: msgCount.get(s.id) || 0,
        cohort_names: cohortsByUser.get(s.user_id) || [],
      }));

      // Filtros que dependen de joins (cohort y búsqueda libre)
      if (filters.cohortId && filters.cohortId !== 'all') {
        const { data: cohortMembers } = await supabase
          .from('cohort_participants')
          .select('user_id')
          .eq('cohort_id', filters.cohortId);
        const memberSet = new Set((cohortMembers || []).map((m: any) => m.user_id));
        rows = rows.filter(r => memberSet.has(r.user_id));
      }

      if (filters.search?.trim()) {
        const term = filters.search.trim().toLowerCase();
        rows = rows.filter(r =>
          r.user_name.toLowerCase().includes(term) ||
          r.user_email.toLowerCase().includes(term) ||
          (r.title || '').toLowerCase().includes(term) ||
          (r.organization_name || '').toLowerCase().includes(term)
        );
      }

      return rows;
    },
  });
}

// Mensajes de una sesión (detalle)
export function useConversationMessages(sessionId: string | null) {
  return useQuery({
    queryKey: ['ai-conversation-messages', sessionId],
    enabled: !!sessionId,
    queryFn: async (): Promise<ConversationMessage[]> => {
      const { data, error } = await supabase
        .from('chat_messages')
        .select('id, role, content, model, metadata, created_at')
        .eq('session_id', sessionId!)
        .order('created_at', { ascending: true });
      if (error) throw error;
      return data || [];
    },
  });
}

// Listas para selects (organizations y cohorts visibles al usuario)
export function useAuditFilterOptions() {
  const { currentRole, profile } = useAuth();
  const isSuperAdmin = currentRole === 'super_admin';

  const orgsQuery = useQuery({
    queryKey: ['audit-organizations', currentRole, profile?.organization_id],
    enabled: isSuperAdmin || currentRole === 'admin',
    queryFn: async () => {
      let q = supabase.from('organizations').select('id, name').order('name');
      if (!isSuperAdmin && profile?.organization_id) {
        q = q.eq('id', profile.organization_id);
      }
      const { data, error } = await q;
      if (error) throw error;
      return data || [];
    },
  });

  const cohortsQuery = useQuery({
    queryKey: ['audit-cohorts', currentRole, profile?.organization_id],
    enabled: isSuperAdmin || currentRole === 'admin',
    queryFn: async () => {
      let q = supabase.from('cohorts').select('id, name, organization_id').order('name');
      if (!isSuperAdmin && profile?.organization_id) {
        q = q.eq('organization_id', profile.organization_id);
      }
      const { data, error } = await q;
      if (error) throw error;
      return data || [];
    },
  });

  return useMemo(() => ({
    organizations: orgsQuery.data || [],
    cohorts: cohortsQuery.data || [],
    isLoading: orgsQuery.isLoading || cohortsQuery.isLoading,
  }), [orgsQuery.data, cohortsQuery.data, orgsQuery.isLoading, cohortsQuery.isLoading]);
}
